import { useEffect, useRef } from "react";

/**
 * AutoPlayVideo — reliable autoplay video for iOS Safari, Android Chrome, and desktop.
 *
 * Why this exists: React sometimes doesn't apply `muted` as a DOM property fast
 * enough on iOS, so `<video autoPlay muted />` can fail to start. We use a ref
 * to (1) force `muted = true` before play, (2) call `.play()` manually,
 * (3) silently catch the promise rejection so Low Power Mode doesn't crash.
 */
export default function AutoPlayVideo({ src, className = "", ariaLabel, testId, ...rest }) {
    const ref = useRef(null);

    useEffect(() => {
        const v = ref.current;
        if (!v) return;
        v.muted = true;
        v.defaultMuted = true;
        v.playsInline = true;
        const tryPlay = () => {
            const p = v.play();
            if (p && typeof p.catch === "function") p.catch(() => {});
        };
        // Try on mount and on first user gesture (covers iOS Low Power Mode).
        tryPlay();
        const onFirstInteract = () => {
            tryPlay();
            window.removeEventListener("touchstart", onFirstInteract);
            window.removeEventListener("click", onFirstInteract);
        };
        window.addEventListener("touchstart", onFirstInteract, { passive: true });
        window.addEventListener("click", onFirstInteract);
        return () => {
            window.removeEventListener("touchstart", onFirstInteract);
            window.removeEventListener("click", onFirstInteract);
        };
    }, [src]);

    return (
        <video
            ref={ref}
            src={src}
            autoPlay
            muted
            loop
            playsInline
            // Webkit-specific attribute helps very old iOS versions.
            // eslint-disable-next-line react/no-unknown-property
            webkit-playsinline="true"
            preload="metadata"
            controls={false}
            disablePictureInPicture
            aria-label={ariaLabel}
            data-testid={testId}
            className={className}
            {...rest}
        />
    );
}
